'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}



const services = [
  {
    title: 'Drum Editing',
    desc: 'Transient alignment, timing correction and sample augmentation for a tight, punchy result.',
  },
  {
    title: 'Guitar/Bass Editing',
    desc: 'Timing and performance editing for guitar and bass tracks.',
  },
  {
    title: 'Vocal Comping',
    desc: 'Best takes selected and assembled into a seamless, natural-sounding performance.',
  },
  {
    title: 'Pitch Correction',
    desc: 'Subtle or aggressive pitch correction depending on the genre and your preference.',
  },
  {
    title: 'Synth/Sound Design',
    desc: 'Additional synth layers and sound design to lift your choruses and enhance the overall mix.',
  },
  {
    title: 'Post Effects',
    desc: 'Creative effects processing applied to enhance the energy and character of your tracks.',
  },
  {
    title: 'Session Clean Up',
    desc: 'All tracks labeled, organised, and cleaned up so your session is mix-ready from the first play.',
  },
];

export default function PostProductionPage() {
  useScrollReveal();

  return (
    <div
      style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', overflowX: 'hidden' }}
    >
      <Header />

      <div className="grid-overlay">
        <div className="grid-inner">
          <div className="grid-line-v" />
          <div className="grid-line-v hidden md:block" />
          <div className="grid-line-v hidden lg:block" />
          <div className="grid-line-v" />
        </div>
      </div>

      {/* ─── PAGE HEADER ─── */}
      <section className="pt-32 pb-16 border-b border-primary/10 relative overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/assets/images/Untitled_design_1_-1773262155254.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'rgba(0,0,0,0.78)' }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at top left, rgba(204,0,0,0.08) 0%, transparent 60%)',
          }}
        />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="anim-in-1">
            <span className="section-label block mb-2">// Services</span>
            <span className="font-mono text-xs text-muted tracking-widest uppercase block mb-4">
              04 / Post Production
            </span>
            <h1
              className="font-display font-extrabold uppercase tracking-tighter text-fg-base mb-4"
              style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', lineHeight: '0.95' }}
            >
              Post
              <br />
              <span className="text-gradient-red">Production</span>
            </h1>
            <p className="font-body text-muted text-lg max-w-xl leading-relaxed mt-4">
              Need your recorded tracks edited, comped, tuned or cleaned up? Need an additional
              layer of synths to help lift your choruses? I provide industry standard post
              production for any metal track!
            </p>
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="section-label block mb-3">// What's Included</span>
            <h2
              className="font-display font-extrabold uppercase tracking-tighter text-fg-base"
              style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', lineHeight: '1.05' }}
            >
              From Raw Tracks
              <br />
              <span className="text-gradient-red">To Mix-Ready.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {services.map((service, i) => (
              <div key={i} className="reveal service-card p-6">
                <div className="vertical-beam" />
                <h3 className="font-display font-bold text-fg-base text-base mb-3">
                  {service.title}
                </h3>
                <p className="font-body text-muted text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="reveal border border-primary/20 bg-bg-card/50 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-fg-base text-lg mb-2">Turnaround Time</h3>
              <p className="font-body text-muted text-sm">
                Single track: 24–48 hours · EP (3–5 songs): 3–5 days · Full album: 7–14 days
              </p>
            </div>
            <Link
              href="/contact"
              className="btn-primary px-8 py-4 text-sm inline-flex items-center gap-2 flex-shrink-0"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>


      {/* ─── CTA ─── */}
      <section className="py-20 border-t border-primary/10 relative overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/assets/images/Untitled_design-1773180564124.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'rgba(0,0,0,0.75)' }}
        />
        {/* Glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(204,0,0,0.08) 0%, transparent 70%)',
          }}
        />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <div className="reveal">
            <h2
              className="font-display font-extrabold uppercase tracking-tighter text-fg-base mb-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '1' }}
            >
              Ready to Clean Up?
            </h2>
            <p className="font-body text-muted mb-8">
              Send your raw session files and I'll have them mix-ready faster than you think.
            </p>
            <Link href="/contact" className="btn-primary px-10 py-4 text-sm inline-block">
              Start Post Production
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
